import { Fragment } from 'react';

import type { DataModelView } from '#core/lib/cognite/view.ts';
import { columnsFromView } from '#core/lib/transforms/column-schema.ts';

import { ViewExternalIdBadgeList } from '#plugin-chat/components/tool-display/ViewExternalIdBadge.tsx';
import {
  buildDescribeViewsInputSchema,
  type DescribeViewsInput,
  type DescribeViewsOutput,
} from '#plugin-chat/components/tools/describe-views/schema.ts';
import { withMention } from '#plugin-chat/components/tools/helpers/withMention.ts';
import { QueryKnowledgeGraphTool } from '#plugin-chat/components/tools/query-knowledge-graph/QueryKnowledgeGraphTool.tsx';
import { ColumnSchemaTable, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { ViewNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * LLM tool that returns the schema (description + typed columns) for one or
 * more CDF Data Model views.
 *
 * Complements `QueryKnowledgeGraphTool` by giving the agent a cheap way to
 * discover which properties a view exposes and their types *before*
 * constructing a query — avoiding filter/projection trial-and-error against
 * the knowledge graph.
 *
 * `execute` is a no-op stub returning `{success: true}`; the real work
 * happens in `message.result`, which formats per-view column tables for
 * the LLM-visible system message.
 *
 * `views` flows in as a prop from the mounting route
 * (`<Tool.DescribeViews views={views} />`) so the parameter schema's
 * view-enum can constrain the LLM to actually-mounted views at
 * registration time.
 */
export const DescribeViewsTool = withMention(
  'explainViews',
  function DescribeViewsToolImpl({ views }: { readonly views: readonly DataModelView[] }) {
    // Empty `views` is a routing/config bug — `buildDescribeViewsInputSchema`
    // requires a non-empty enum tuple. Surface as a null mount rather than
    // crash the chat tree.
    if (views.length === 0) {
      return null;
    }
    const viewIds = views.map((v) => v.externalId) as [string, ...string[]];
    const parametersSchema = buildDescribeViewsInputSchema(viewIds);
    const viewsById = new Map(views.map((v) => [v.externalId, v]));

    return (
      <Tool<DescribeViewsInput, DescribeViewsOutput>
        description={
          <ToolGuidance
            description="Returns view description and a markdown table of columns with type and per-column description for each view."
            tool={DescribeViewsTool}
            whenToUse={[
              `Before calling ${QueryKnowledgeGraphTool.mention} to discover which properties a view exposes and their types`,
              'When you need to pick the right property names for filters or projections',
            ]}
          />
        }
        display={{
          running: ({ args }: { args: DescribeViewsInput }) => (
            <>
              <span>Fetching view schema...</span>
              <ViewExternalIdBadgeList externalIds={args.views ?? []} />
            </>
          ),
          complete: {
            label: 'View schema fetched',
            semantic: 'view-schema',
            extras: ({ args }: { args: DescribeViewsInput }) => (
              <ViewExternalIdBadgeList externalIds={args.views ?? []} />
            ),
          },
          progress: ({ args }: { args: DescribeViewsInput }) => {
            const count = args.views?.length ?? 0;
            if (count === 0) {
              return <>fetching view schema...</>;
            }
            return (
              <>
                fetching {count} view schema{count === 1 ? '' : 's'}...
              </>
            );
          },
        }}
        execute={(): DescribeViewsOutput => ({ success: true as const })}
        message={{
          call: ({ args }: { args: DescribeViewsInput }) => (
            <p>Looking up view schema: {[...new Set(args.views)].join(', ')}.</p>
          ),
          result: ({ args }: { args: DescribeViewsInput }) => (
            <>
              <p>
                Resolved schema for {new Set(args.views).size} view(s) — use these exact column names/kinds when writing{' '}
                <code>queryKnowledgeGraph</code> expressions:
              </p>
              {[...new Set(args.views)].map((externalId) => {
                const view = viewsById.get(externalId);
                if (!view) {
                  throw new ViewNotFoundError(externalId, viewIds);
                }
                const viewDescription =
                  (view.schema.out as { description?: string }).description ?? `Data model view: ${externalId}`;
                const columns = columnsFromView(view);
                return (
                  <Fragment key={externalId}>
                    <h2>{externalId}</h2>
                    <p>{viewDescription}</p>
                    <ColumnSchemaTable columns={columns} />
                  </Fragment>
                );
              })}
            </>
          ),
        }}
        name="explainViews"
        parameters={parametersSchema}
      />
    );
  },
);
